"use client";

import Image from "next/image";
import React, { useEffect, useMemo, useState } from "react";
import { Great_Vibes, Cormorant_Garamond } from "next/font/google";
import GalleryQrOverlay from "@/app/components/GalleryQrOverlay";
import MediaQrCode from "@/app/components/MediaQrCode";
import SharePanel from "@/app/components/SharePanel";
import SessionBackLink from "@/app/components/SessionBackLink";
import { getAlbumShortPath, getPhotoShortPath } from "@/app/lib/shareLinks";
import {
  COUPLE_PASSWORD,
  getSession,
  SUPERADMIN_PASSWORD,
  type PortalSession,
} from "@/app/lib/weddingPortal";

const greatVibes = Great_Vibes({
  subsets: ["latin"],
  weight: "400",
});

const cormorant = Cormorant_Garamond({
  subsets: ["latin"],
  weight: ["400", "500", "600", "700"],
});

type PhotoItem = {
  id: string;
  src: string;
  title: string;
  createdAt?: string;
  source?: "photos" | "framed" | "booth";
  sharePath?: string;
  downloadPath?: string;
  fullscreenPath?: string;
  qrPath?: string;
  publicPagePath?: string;
  originalName?: string;
  sizeBytes?: number;
};

type ApiMediaItem = {
  id: string;
  url: string;
  type: "image" | "video";
  title: string;
  createdAt?: string;
  sharePath?: string;
  downloadPath?: string;
  fullscreenPath?: string;
  qrPath?: string;
  publicPagePath?: string;
  originalName?: string;
  sizeBytes?: number;
};

const GALLERY_REFRESH_MS = 10000;

type GallerySort = "newest" | "oldest" | "title" | "largest";

const starterItems: PhotoItem[] = [
  { id: "1", src: "/johnmax.jpg", title: "John & Maxine", source: "photos" },
];

function isPhotoItem(item: unknown): item is PhotoItem {
  if (!item || typeof item !== "object") return false;

  const candidate = item as Partial<PhotoItem>;
  return typeof candidate.id === "string" && typeof candidate.src === "string" && typeof candidate.title === "string";
}

function getTimestamp(item: PhotoItem) {
  return item.createdAt ? new Date(item.createdAt).getTime() || 0 : 0;
}

function sortNewestFirst(items: PhotoItem[]) {
  return [...items].sort((a, b) => getTimestamp(b) - getTimestamp(a));
}

function sortPhotos(items: PhotoItem[], sortOrder: GallerySort) {
  return [...items].sort((a, b) => {
    if (sortOrder === "oldest") return getTimestamp(a) - getTimestamp(b);
    if (sortOrder === "title") return a.title.localeCompare(b.title);
    if (sortOrder === "largest") return (b.sizeBytes ?? 0) - (a.sizeBytes ?? 0);
    return getTimestamp(b) - getTimestamp(a);
  });
}

function formatBytes(bytes?: number) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getInitialItems(): PhotoItem[] {
  if (typeof window === "undefined") {
    return starterItems;
  }

  try {
    const savedRaw = window.localStorage.getItem("weddingPhotoUploads");
    if (!savedRaw) return starterItems;

    const saved = JSON.parse(savedRaw) as unknown;
    if (Array.isArray(saved)) {
      return sortNewestFirst([...saved.filter(isPhotoItem), ...starterItems]);
    }
  } catch (error) {
    console.error(error);
  }

  return starterItems;
}

function getInitialPageParams() {
  if (typeof window === "undefined") {
    return {
      focusId: null as string | null,
      filter: null as string | null,
    };
  }

  const searchParams = new URLSearchParams(window.location.search);

  return {
    focusId: searchParams.get("focus"),
    filter: searchParams.get("filter"),
  };
}

function getPhotoSharePath(item: PhotoItem) {
  return item.sharePath ?? getPhotoShortPath(item.id);
}

function getPhotoDownloadPath(item: PhotoItem) {
  return item.downloadPath ?? item.src;
}

function getPhotoFullscreenPath(item: PhotoItem) {
  return item.fullscreenPath ?? item.src;
}

function getPhotoQrPath(item: PhotoItem) {
  return item.qrPath ?? getPhotoSharePath(item);
}

function getTokenFromSharePath(path?: string) {
  const match = path?.match(/\/m\/([^/?#]+)/);
  return match?.[1] ?? null;
}

function getPhotoPublicPagePath(item: PhotoItem) {
  const token = getTokenFromSharePath(item.sharePath);
  return item.publicPagePath ?? (token ? `/wedding/photos/${token}` : getPhotoSharePath(item));
}

function getSourceLabel(item: PhotoItem) {
  if (item.source === "framed") return "Framed Keepsake";
  if (item.source === "booth") return "Photo Booth";
  return "Wedding Photo";
}

function getDeletePassword(session: PortalSession | null) {
  if (session?.role === "superadmin") return SUPERADMIN_PASSWORD;
  if (session?.role === "couple") return COUPLE_PASSWORD;
  return "";
}

function isServerManagedPhoto(item: PhotoItem) {
  return Boolean(item.sharePath || item.src.startsWith("/api/"));
}

function getUploadedPhotos(items: ApiMediaItem[] = []): PhotoItem[] {
  return items
    .filter((item) => item.type === "image")
    .map((item) => ({
      id: item.id,
      src: item.url,
      title: item.title,
      createdAt: item.createdAt,
      source: "photos" as const,
      sharePath: item.sharePath,
      downloadPath: item.downloadPath,
      fullscreenPath: item.fullscreenPath,
      qrPath: item.qrPath,
      publicPagePath: item.publicPagePath,
      originalName: item.originalName,
      sizeBytes: item.sizeBytes,
    }));
}

function mergePhotoItems(uploadedPhotos: PhotoItem[]) {
  const merged = [...uploadedPhotos];
  getInitialItems().forEach((item) => {
    if (!merged.some((existing) => existing.id === item.id)) {
      merged.push(item);
    }
  });

  return sortNewestFirst(merged);
}

function formatDate(value?: string) {
  if (!value) return "Wedding Memory";

  try {
    return new Intl.DateTimeFormat("en-GB", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(value));
  } catch {
    return "Wedding Memory";
  }
}

function ActionLink({
  href,
  children,
  primary = false,
}: {
  href: string;
  children: React.ReactNode;
  primary?: boolean;
}) {
  return (
    <a
      href={href}
      className={
        primary
          ? "inline-flex min-h-12 items-center justify-center rounded-full bg-[#1f4e79] px-4 py-3 text-xs font-bold uppercase tracking-[0.18em] text-white shadow-sm transition hover:bg-[#173d61]"
          : "inline-flex min-h-12 items-center justify-center rounded-full border border-[#caa23a]/60 bg-[#fffaf0] px-4 py-3 text-xs font-bold uppercase tracking-[0.18em] text-[#1f4e79] transition hover:bg-[#f2ecdf]"
      }
    >
      {children}
    </a>
  );
}

export default function PhotosPage() {
  const [items, setItems] = useState<PhotoItem[]>(getInitialItems);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);
  const [{ focusId, filter }] = useState(getInitialPageParams);
  const [selectedId, setSelectedId] = useState<string | null>(focusId);
  const [portalSession, setPortalSession] = useState<PortalSession | null>(null);
  const [sortOrder, setSortOrder] = useState<GallerySort>("newest");
  const [manageStatus, setManageStatus] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    setPortalSession(getSession());

    function handleStorage() {
      setPortalSession(getSession());
    }

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  useEffect(() => {
    let active = true;

    async function loadPhotos() {
      try {
        const response = await fetch("/api/media", { cache: "no-store" });
        const json = (await response.json()) as { items?: ApiMediaItem[] };

        if (!active) return;

        setItems(mergePhotoItems(getUploadedPhotos(json.items)));
        setLastUpdatedAt(new Date().toISOString());
      } catch (error) {
        console.error(error);
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    }

    void loadPhotos();
    const intervalId = window.setInterval(loadPhotos, GALLERY_REFRESH_MS);
    const refreshOnFocus = () => void loadPhotos();
    const refreshOnVisibility = () => {
      if (document.visibilityState === "visible") {
        void loadPhotos();
      }
    };

    window.addEventListener("focus", refreshOnFocus);
    window.addEventListener("storage", refreshOnFocus);
    document.addEventListener("visibilitychange", refreshOnVisibility);

    return () => {
      active = false;
      window.clearInterval(intervalId);
      window.removeEventListener("focus", refreshOnFocus);
      window.removeEventListener("storage", refreshOnFocus);
      document.removeEventListener("visibilitychange", refreshOnVisibility);
    };
  }, []);

  const visibleItems = useMemo(() => {
    let nextItems = items;

    if (focusId) {
      nextItems = items;
    } else if (filter === "framed") {
      nextItems = items.filter((item) => item.source === "framed");
    } else if (filter === "photos") {
      nextItems = items.filter((item) => item.source !== "framed");
    }

    return sortPhotos(nextItems, sortOrder);
  }, [filter, focusId, items, sortOrder]);

  useEffect(() => {
    if (focusId && visibleItems.some((item) => item.id === focusId)) {
      setSelectedId(focusId);
    }
  }, [focusId, visibleItems]);

  useEffect(() => {
    if (selectedId && !visibleItems.some((item) => item.id === selectedId)) {
      setSelectedId(null);
    }
  }, [selectedId, visibleItems]);

  const selectedIndex = selectedId ? visibleItems.findIndex((item) => item.id === selectedId) : -1;
  const selectedItem = selectedIndex >= 0 ? visibleItems[selectedIndex] : null;
  const selectedPublicPageLink = selectedItem ? getPhotoPublicPagePath(selectedItem) : "";

  function openPreview(id: string) {
    setSelectedId(id);
  }

  function closePreview() {
    setSelectedId(null);
  }

  function goPrev() {
    if (selectedIndex < 0) return;
    const nextIndex = (selectedIndex - 1 + visibleItems.length) % visibleItems.length;
    setSelectedId(visibleItems[nextIndex]?.id ?? null);
  }

  function goNext() {
    if (selectedIndex < 0) return;
    const nextIndex = (selectedIndex + 1) % visibleItems.length;
    setSelectedId(visibleItems[nextIndex]?.id ?? null);
  }

  function deleteLocalPhoto(item: PhotoItem) {
    try {
      const raw = window.localStorage.getItem("weddingPhotoUploads");
      const parsed = raw ? (JSON.parse(raw) as unknown) : [];
      if (Array.isArray(parsed)) {
        window.localStorage.setItem(
          "weddingPhotoUploads",
          JSON.stringify(parsed.filter((entry) => !isPhotoItem(entry) || entry.id !== item.id))
        );
      }
    } catch {}

    setItems((current) => current.filter((entry) => entry.id !== item.id));
    if (selectedId === item.id) setSelectedId(null);
    setManageStatus("Photo removed from this browser.");
  }

  async function deletePhoto(item: PhotoItem) {
    if (!portalSession) return;

    const confirmed = window.confirm(`Delete "${item.title}" permanently?`);
    if (!confirmed) return;

    if (!isServerManagedPhoto(item)) {
      deleteLocalPhoto(item);
      return;
    }

    setDeletingId(item.id);
    setManageStatus("Deleting photo...");

    try {
      const response = await fetch("/api/media/manage", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: item.id,
          password: getDeletePassword(portalSession),
        }),
      });
      const json = (await response.json()) as {
        items?: ApiMediaItem[];
        error?: string;
      };

      if (!response.ok || !json.items) {
        throw new Error(json.error || "Unable to delete this photo.");
      }

      setItems(mergePhotoItems(getUploadedPhotos(json.items)));
      if (selectedId === item.id) setSelectedId(null);
      setManageStatus("Photo deleted.");
    } catch (error) {
      console.error(error);
      setManageStatus(error instanceof Error ? error.message : "Unable to delete this photo.");
    } finally {
      setDeletingId(null);
    }
  }

  const pageTitle =
    filter === "framed" ? "Framed Photos" : filter === "photos" ? "Photo Gallery" : "Photo Gallery";
  const sharePath = filter === "framed" ? getAlbumShortPath("framed") : getAlbumShortPath("photos");

  return (
    <main
      className={`min-h-screen bg-[radial-gradient(circle_at_top_left,rgba(31,78,121,0.10),transparent_34%),linear-gradient(180deg,#fbfaf7_0%,#f1eee6_100%)] px-4 py-6 text-slate-800 sm:px-6 lg:px-8 ${cormorant.className}`}
    >
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex items-center justify-between gap-4">
          <SessionBackLink />

          <p className="text-center text-xs font-semibold uppercase tracking-[0.28em] text-[#8c8c86]">
            John &amp; Maxine
          </p>

          <div className="w-20" />
        </div>

        <section className="relative mb-8 overflow-hidden rounded-[2.25rem] border border-[#decf9b] bg-white/85 px-5 py-8 text-center shadow-[0_24px_80px_rgba(15,23,42,0.10)] sm:px-10 lg:px-16">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_20%,rgba(202,162,58,0.18),transparent_24%),radial-gradient(circle_at_84%_18%,rgba(31,78,121,0.14),transparent_26%)]" />
          <div className="relative">
            <p className="text-xs font-semibold uppercase tracking-[0.34em] text-[#8c8c86]">
              Newest First · Live Updates
            </p>
            <h1 className={`${greatVibes.className} wedding-gold-halo mt-4 text-6xl leading-none text-[#1f4e79] sm:text-7xl lg:text-8xl`}>
              {pageTitle}
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-base leading-8 text-[#686861] sm:text-lg">
              {filter === "framed"
                ? "Browse the framed keepsake photos captured during the celebration."
                : "Browse the newest shared wedding photos, ready to preview, download, share, open fullscreen, or scan by QR code."}
            </p>

            <div className="mt-6 grid gap-3 sm:grid-cols-3">
              <div className="rounded-3xl border border-[#ead8a0] bg-[#fffaf0]/80 px-4 py-4">
                <p className="text-xs uppercase tracking-[0.22em] text-[#8c8c86]">Photos</p>
                <p className="mt-1 text-3xl font-semibold text-[#1f3f6d]">{visibleItems.length}</p>
              </div>
              <div className="rounded-3xl border border-[#ead8a0] bg-[#fffaf0]/80 px-4 py-4">
                <p className="text-xs uppercase tracking-[0.22em] text-[#8c8c86]">Refresh</p>
                <p className="mt-1 text-lg font-semibold text-[#1f3f6d]">Automatic</p>
              </div>
              <div className="rounded-3xl border border-[#ead8a0] bg-[#fffaf0]/80 px-4 py-4">
                <p className="text-xs uppercase tracking-[0.22em] text-[#8c8c86]">Updated</p>
                <p className="mt-1 text-lg font-semibold text-[#1f3f6d]">
                  {lastUpdatedAt ? formatDate(lastUpdatedAt) : "Loading"}
                </p>
              </div>
            </div>
          </div>
        </section>

        <div className="mb-8">
          <SharePanel title={`${pageTitle} Album`} shortPath={sharePath} />
        </div>

        <section className="mb-8 rounded-[1.75rem] border border-[#decf9b] bg-white/90 p-4 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#8c8c86]">
                Gallery Controls
              </p>
              <p className="mt-2 text-sm leading-6 text-[#686861]">
                Sort photos for quick review. Delete controls appear only when logged in.
              </p>
            </div>

            <div className="grid gap-2 sm:grid-cols-4">
              {(["newest", "oldest", "title", "largest"] as const).map((nextSort) => (
                <button
                  key={nextSort}
                  type="button"
                  onClick={() => setSortOrder(nextSort)}
                  className={
                    sortOrder === nextSort
                      ? "min-h-11 rounded-full bg-[#1f4e79] px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] text-white"
                      : "min-h-11 rounded-full border border-[#caa23a]/60 bg-[#fffaf0] px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] text-[#1f4e79]"
                  }
                >
                  {nextSort === "title"
                    ? "A-Z"
                    : nextSort === "largest"
                      ? "Largest"
                      : nextSort}
                </button>
              ))}
            </div>
          </div>

          {portalSession ? (
            <p className="mt-4 rounded-2xl border border-[#ead8a0] bg-[#fffaf0] px-4 py-3 text-sm font-semibold text-[#1f4e79]">
              Manage mode active as {portalSession.role === "superadmin" ? "admin" : "couple"}.
            </p>
          ) : null}

          {manageStatus ? (
            <p className="mt-3 rounded-2xl border border-[#ead8a0] bg-[#fffdfa] px-4 py-3 text-sm text-[#1f4e79]" role="status">
              {manageStatus}
            </p>
          ) : null}
        </section>

        {isLoading ? (
          <div className="rounded-[2rem] border border-[#decf9b] bg-white px-6 py-12 text-center shadow-sm">
            <p className="text-lg text-[#686861]">Loading the latest wedding photos...</p>
          </div>
        ) : visibleItems.length === 0 ? (
          <div className="rounded-[2rem] border border-[#decf9b] bg-white px-6 py-12 text-center shadow-sm">
            <p className="text-lg text-[#686861]">No photos have been uploaded yet.</p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {visibleItems.map((item) => {
              const shareLink = getPhotoSharePath(item);
              const qrLink = getPhotoQrPath(item);
              const publicPageLink = getPhotoPublicPagePath(item);

              return (
                <article
                  key={item.id}
                  className="group overflow-hidden rounded-[2rem] border border-[#decf9b] bg-white shadow-[0_18px_55px_rgba(15,23,42,0.09)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_26px_70px_rgba(15,23,42,0.13)]"
                >
                  <div className="relative aspect-[4/3] w-full overflow-hidden bg-[#ebe7df]">
                    <button
                      type="button"
                      onClick={() => openPreview(item.id)}
                      className="absolute inset-0 block w-full text-left"
                      aria-label={`View full image ${item.title}`}
                    >
                      <Image
                        src={item.src}
                        alt={item.title}
                        fill
                        className="object-cover transition duration-500 group-hover:scale-[1.04]"
                        unoptimized={item.src.startsWith("data:") || item.src.startsWith("/api/")}
                      />
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-5 text-white">
                        <p className="text-xs uppercase tracking-[0.22em] text-[#f8dc91]">{getSourceLabel(item)}</p>
                        <h2 className="mt-2 line-clamp-2 text-2xl font-semibold">{item.title}</h2>
                        <p className="mt-1 text-sm text-white/80">
                          {formatDate(item.createdAt)}
                          {item.sizeBytes ? ` · ${formatBytes(item.sizeBytes)}` : ""}
                        </p>
                      </div>
                    </button>

                    <GalleryQrOverlay path={publicPageLink} mediaType="photo" />
                  </div>

                  <div className="space-y-4 p-5">
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <button
                        type="button"
                        onClick={() => openPreview(item.id)}
                        className="inline-flex min-h-12 items-center justify-center rounded-full border border-[#caa23a]/60 bg-[#fffaf0] px-4 py-3 text-xs font-bold uppercase tracking-[0.18em] text-[#1f4e79] transition hover:bg-[#f2ecdf]"
                      >
                        View Full Image
                      </button>
                      <ActionLink href={getPhotoDownloadPath(item)} primary>
                        Download
                      </ActionLink>
                      <ActionLink href={shareLink}>Share</ActionLink>
                      <ActionLink href={getPhotoFullscreenPath(item)}>Fullscreen</ActionLink>
                      {portalSession ? (
                        <button
                          type="button"
                          onClick={() => void deletePhoto(item)}
                          disabled={deletingId === item.id}
                          className="inline-flex min-h-12 items-center justify-center rounded-full bg-[#a85249] px-4 py-3 text-xs font-bold uppercase tracking-[0.18em] text-white transition hover:bg-[#7a3029] disabled:cursor-not-allowed disabled:opacity-60 sm:col-span-2"
                        >
                          {deletingId === item.id ? "Deleting..." : "Delete Photo"}
                        </button>
                      ) : null}
                    </div>

                    <div className="grid gap-4 rounded-[1.5rem] border border-[#ead8a0] bg-[#fcfbf8] p-4 sm:grid-cols-[auto_1fr] sm:items-center">
                      <div className="mx-auto rounded-2xl bg-[#fffaf0] p-2 shadow-inner">
                        <MediaQrCode path={publicPageLink} size={120} />
                      </div>
                      <div className="text-center sm:text-left">
                        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8c8c86]">QR Code</p>
                        <p className="mt-2 text-sm leading-6 text-[#686861]">Scan to download this photo.</p>
                        <a
                          href={qrLink}
                          className="mt-3 inline-flex min-h-10 items-center justify-center rounded-full border border-[#caa23a]/60 bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#1f4e79] transition hover:bg-[#f2ecdf]"
                        >
                          Open QR Code
                        </a>
                      </div>
                    </div>

                    <SharePanel title={item.title} shortPath={shareLink} imageSrc={item.src} />
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      {selectedItem ? (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/80 p-3 sm:items-center sm:p-4"
          onClick={closePreview}
        >
          <div
            className="relative my-3 max-h-[calc(100dvh-1.5rem)] w-full max-w-6xl overflow-y-auto rounded-[1.5rem] border border-[#decf9b] bg-white shadow-2xl sm:my-0 sm:rounded-[2rem]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 border-b border-[#ead8a0] px-4 py-4 sm:px-6">
              <button
                type="button"
                onClick={goPrev}
                className="rounded-full border border-[#caa23a]/60 bg-[#f8f5ed] px-4 py-2 text-sm font-semibold text-[#1f4e79] transition hover:bg-[#f2ecdf]"
              >
                Back
              </button>

              <div className="text-center">
                <p className="text-xs uppercase tracking-[0.22em] text-[#8c8c86]">John &amp; Maxine</p>
                <p className="text-lg font-semibold text-[#1f3f6d]">{selectedItem.title}</p>
              </div>

              <button
                type="button"
                onClick={goNext}
                className="rounded-full border border-[#caa23a]/60 bg-[#f8f5ed] px-4 py-2 text-sm font-semibold text-[#1f4e79] transition hover:bg-[#f2ecdf]"
              >
                Forward
              </button>
            </div>

            <div className="grid gap-4 bg-[#ebe7df] p-4 lg:grid-cols-[1fr_260px] sm:p-6">
              <div className="relative flex max-h-[72vh] min-h-80 items-center justify-center">
                <div className="relative h-[64vh] w-full max-w-5xl">
                  <Image
                    src={selectedItem.src}
                    alt={selectedItem.title}
                    fill
                    className="object-contain"
                    unoptimized={selectedItem.src.startsWith("data:") || selectedItem.src.startsWith("/api/")}
                  />
                </div>
              </div>

              <aside className="flex flex-col items-center justify-center rounded-[1.5rem] border border-[#ead8a0] bg-[#fffaf0] p-4 text-center">
                <MediaQrCode path={selectedPublicPageLink} size={190} />
                <p className="mt-3 text-sm font-semibold leading-6 text-[#1f3f6d]">
                  Scan to download this photo
                </p>
                <a
                  href={selectedPublicPageLink}
                  className="mt-3 inline-flex min-h-10 items-center justify-center rounded-full border border-[#caa23a]/60 bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] text-[#1f4e79] transition hover:bg-[#f2ecdf]"
                >
                  Open Photo Page
                </a>
              </aside>
            </div>

            <div className="grid gap-4 border-t border-[#ead8a0] px-4 py-4 lg:grid-cols-[1fr_auto] lg:items-center sm:px-6">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6">
                <button
                  type="button"
                  onClick={closePreview}
                  className="inline-flex min-h-12 items-center justify-center rounded-full border border-[#caa23a]/60 bg-[#fffaf0] px-4 py-3 text-xs font-bold uppercase tracking-[0.18em] text-[#1f4e79]"
                >
                  Back to Gallery
                </button>
                <ActionLink href={getPhotoDownloadPath(selectedItem)} primary>
                  Download
                </ActionLink>
                <ActionLink href={getPhotoSharePath(selectedItem)}>Share</ActionLink>
                <ActionLink href={getPhotoFullscreenPath(selectedItem)}>Fullscreen</ActionLink>
                <ActionLink href={getPhotoQrPath(selectedItem)}>QR Code</ActionLink>
                {portalSession ? (
                  <button
                    type="button"
                    onClick={() => void deletePhoto(selectedItem)}
                    disabled={deletingId === selectedItem.id}
                    className="inline-flex min-h-12 items-center justify-center rounded-full bg-[#a85249] px-4 py-3 text-xs font-bold uppercase tracking-[0.18em] text-white disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {deletingId === selectedItem.id ? "Deleting..." : "Delete"}
                  </button>
                ) : null}
              </div>

              <div className="mx-auto rounded-2xl border border-[#ead8a0] bg-[#fffaf0] p-2 lg:hidden">
                <MediaQrCode path={selectedPublicPageLink} size={120} />
              </div>
            </div>

            <div className="border-t border-[#ead8a0] px-4 py-4 sm:px-6">
              <SharePanel
                title={selectedItem.title}
                shortPath={getPhotoSharePath(selectedItem)}
                imageSrc={selectedItem.src}
              />
            </div>
          </div>
        </div>
      ) : null}
    </main>
  );
}
